import React, { useState } from "react";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import { ThemeType } from "@markit/common.types";
import { useTheme } from "../hooks/useTheme";

export type CheckoutSummaryLineItem = {
  label: React.ReactNode;
  // Pre-formatted amount (e.g. "$10.00", "-$5.00") — each caller formats with
  // its own currency helper.
  value: React.ReactNode;
};

type CheckoutTotalSummaryProps = {
  // Pre-formatted, tax/fee-inclusive total (e.g. "$10.63", or "--").
  totalValue: React.ReactNode;
  // Breakdown rows shown when expanded (e.g. Subtotal/Discount/Tax, or Ticket
  // Price/Discount/Service Fee).
  lineItems: CheckoutSummaryLineItem[];
  totalLabel?: React.ReactNode;
  // Whether the chevron shows and the breakdown can expand. Defaults to whether
  // there are any line items.
  expandable?: boolean;
  // Overrides for the total amount (e.g. red when below the Stripe minimum).
  totalValueStyle?: React.CSSProperties;
  onTotalValuePress?: () => void;
  themeType?: ThemeType;
};

/**
 * Shared checkout summary: a bold Total row with a collapsible breakdown of
 * line items. Used across purchase flows (event tickets, subscriptions, ...) so
 * the totals UI stays identical; callers supply their own labels/amounts.
 */
const CheckoutTotalSummary = (props: CheckoutTotalSummaryProps) => {
  const {
    totalValue,
    lineItems,
    totalLabel = "Total",
    expandable = lineItems.length > 0,
    totalValueStyle,
    onTotalValuePress,
    themeType,
  } = props;
  const { theme } = useTheme(themeType);
  const [showAmountDetails, setShowAmountDetails] = useState(false);

  return (
    <div className="ColumnNormal">
      <div className="AlignedRowSpaced" style={theme.PrimaryText}>
        <span
          className="AlignedRow sectionTitle"
          onClick={() => expandable && setShowAmountDetails(!showAmountDetails)}
          style={{ cursor: expandable ? "pointer" : "auto" }}
        >
          {totalLabel}
          {expandable ? (
            <span style={{ marginLeft: 5, marginTop: 5 }}>
              {showAmountDetails ? (
                <FiChevronUp size={20} style={theme.SubText} />
              ) : (
                <FiChevronDown size={20} style={theme.SubText} />
              )}
            </span>
          ) : null}
        </span>
        <span
          onClick={onTotalValuePress}
          style={{
            fontWeight: 600,
            fontSize: 20,
            color: theme.PrimaryText.color,
            cursor: onTotalValuePress ? "pointer" : "auto",
            ...totalValueStyle,
          }}
        >
          {totalValue}
        </span>
      </div>
      {expandable && showAmountDetails ? (
        <div className="ColumnNormal" style={{ gap: 10, paddingTop: 10 }}>
          {lineItems.map((item, index) => (
            <div className="AlignedRowSpaced" key={index}>
              <span className="bodySubtext">{item.label}</span>
              <span className="bodySubtext">{item.value}</span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default CheckoutTotalSummary;
